import { useMutation } from '@apollo/client/react';

import { INSERT_IMAGE_ONE } from '@modules/_shared/components/image-gallery/imageGalleryQueries';
import {
  uploadFileAsync,
  useFileUpload,
} from '@modules/_shared/utils/files/file-upload';
import { useLocale } from '@shared/hooks/locale';

export type UploadedImage = { id: string; url: string };

export const useImageRegistration = () => {
  const [insertImage] = useMutation(INSERT_IMAGE_ONE);

  const registerImage = async (url: string) => {
    const { data } = await insertImage({ variables: { url } });
    const image = data?.insert_images_one;

    return image ?? null;
  };

  return { registerImage };
};

export const useImageUpload = ({
  onError,
}: {
  onError: (message: string) => void;
}) => {
  const { t } = useLocale();
  const [uploadFile] = useFileUpload('image');
  const { registerImage } = useImageRegistration();

  const upload = async (file: File): Promise<UploadedImage | null> => {
    try {
      const uploaded = await uploadFileAsync(uploadFile, file);
      const image = await registerImage(uploaded.url);
      if (!image?.id || !image.url) {
        throw new Error('Server did not return the saved image');
      }
      return { id: image.id, url: image.url };
    } catch {
      onError(t('We could not upload your image. Please try again.'));
      return null;
    }
  };

  return { upload };
};
